import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LogIn, User, Lock, Eye, EyeOff, Loader2, Mail, X, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { cognitoLogin, cognitoLogout, cognitoConfirmSignup, cognitoResendCode, isUserNotConfirmedError } from '../lib/cognito-auth';
import { logger } from '../services/logger.service';
import { toast } from 'sonner';

interface AgentLoginProps {
  onLoginSuccess: () => void;
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

type AgentView = 'login' | 'verify-email';

export function AgentLogin({ onLoginSuccess, onBack, onNavigate }: AgentLoginProps) {
  const [view, setView] = useState<AgentView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Verification state
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Trim whitespace from credentials
      const { user } = await cognitoLogin(email.trim(), password.trim());

      if (user.role !== 'agent') {
        // Login succeeded but wrong role — clean up the session
        // to avoid leaving the user in an inconsistent state
        await cognitoLogout();
        setError('This account is not registered as an agent. Please use the main login instead.');
        setIsLoading(false);
        return;
      }

      toast.success(`Welcome back, ${user.name}!`, {
        description: 'Logged in to Agent Hub',
      });

      onLoginSuccess();
    } catch (err: any) {
      logger.error('Agent login failed', err as Error, undefined, 'AgentLogin');

      if (isUserNotConfirmedError(err)) {
        setVerifyError('');
        setVerifyCode('');
        setView('verify-email');
        toast.info('Email verification required', {
          description: 'Please enter the verification code sent to your email',
        });
      } else if (err.statusCode === 500 || err.statusCode === 502 || err.isServerError) {
        // Server error - show user-friendly message
        setError(err.message || 'We\'re experiencing technical difficulties. Please try again later.');

        // Log details for developers (CloudWatch logging)
        logger.error('Server Error - Full Details', err as Error, undefined, 'AgentLogin');
      } else {
        // Other errors (bad credentials, network, etc.)
        setError(err.message || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError('');
    setVerifyLoading(true);

    try {
      const { user } = await cognitoConfirmSignup(email, verifyCode, password);

      if (user.role !== 'agent') {
        await cognitoLogout();
        setView('login');
        setError('This account is not registered as an agent. Please use the main login instead.');
        setVerifyLoading(false);
        return;
      }

      toast.success(`Email verified! Welcome, ${user.name}!`, {
        description: 'Logged in to Agent Hub via Cognito',
      });

      onLoginSuccess();
    } catch (err: any) {
      logger.error('Agent email verification error', err as Error, undefined, 'AgentLogin');
      setVerifyError(err.message || 'Verification failed. Please check your code and try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResendLoading(true);

    try {
      await cognitoResendCode(email);
      toast.success('Verification code resent!', {
        description: `A new code has been sent to ${email}`,
      });
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      logger.error('Resend code error', err as Error, undefined, 'AgentLogin');
      toast.error('Failed to resend code', {
        description: err.message || 'Please try again later',
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl border-2 border-primary/20">
          <CardHeader className="space-y-1 text-center relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 hover:bg-muted/50"
              onClick={onBack}
              data-testid="agent-login-close-button"
            >
              <X className="w-4 h-4" />
            </Button>

            <AnimatePresence mode="wait">
              {view === 'login' && (
                <motion.div key="login-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Agent Login</CardTitle>
                  <CardDescription>
                    Sign in with your Cognito account to access the Agent Hub
                  </CardDescription>
                </motion.div>
              )}

              {view === 'verify-email' && (
                <motion.div key="verify-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[#FDB022]/10 flex items-center justify-center">
                    <Mail className="h-8 w-8 text-[#FDB022]" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Verify Your Email</CardTitle>
                  <CardDescription>
                    Enter the verification code sent to <span className="font-medium text-foreground">{email}</span>
                  </CardDescription>
                </motion.div>
              )}
            </AnimatePresence>
          </CardHeader>

          <CardContent className="space-y-4">
            <AnimatePresence mode="wait">
              {/* ===================== LOGIN VIEW ===================== */}
              {view === 'login' && (
                <motion.div
                  key="login-view"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="agents@somethingproperty.co.za"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          autoComplete="email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading || !email || !password}
                      data-testid="agent-login-submit-button"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <LogIn className="mr-2 h-4 w-4" />
                          Sign In
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Forgot Password Link */}
                  <div className="text-center -mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (onNavigate) {
                          onBack();
                          onNavigate('forgot-password');
                        }
                      }}
                      className="text-sm text-[#FDB022] hover:underline font-medium"
                    >
                      Forgot your password?
                    </button>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={onBack}
                    data-testid="agent-login-back-to-home-button"
                  >
                    Back to Home
                  </Button>
                </motion.div>
              )}

              {/* ===================== VERIFY EMAIL VIEW ===================== */}
              {view === 'verify-email' && (
                <motion.div
                  key="verify-view"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="rounded-lg bg-[#FDB022]/10 border border-[#FDB022]/30 p-3">
                    <p className="text-sm text-[#FDB022]">
                      We've sent a verification code to your email address. Please check your inbox (and spam folder) and enter the code below.
                    </p>
                  </div>

                  {verifyError && (
                    <Alert variant="destructive">
                      <AlertDescription>{verifyError}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleVerifyEmail} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="agent-verify-code">Verification Code</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="agent-verify-code"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verifyCode}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setVerifyCode(val);
                          }}
                          className="pl-10 text-center text-lg tracking-[0.5em] font-mono"
                          required
                          maxLength={6}
                          autoFocus
                          autoComplete="one-time-code"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={verifyLoading || verifyCode.length < 6}
                      data-testid="agent-login-verify-submit-button"
                    >
                      {verifyLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Verify & Sign In
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="flex items-center justify-center gap-1 text-sm">
                    <span className="text-muted-foreground">Didn't receive the code?</span>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendLoading || resendCooldown > 0}
                      className="text-[#FDB022] hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                    >
                      {resendLoading
                        ? 'Sending...'
                        : resendCooldown > 0
                          ? `Resend in ${resendCooldown}s`
                          : 'Resend Code'
                      }
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => { setView('login'); setVerifyError(''); }}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to Login
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>All agent authentication is managed through AWS Cognito</p>
        </div>
      </motion.div>
    </div>
  );
}